import { type KeyboardEvent, type MouseEvent, type ReactNode, useState } from 'react';
import { Menu, type MenuProps, ScrollArea, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

export type SearchableMenuOption = {
  id: string;
  label: string;
  /**
   * Shown at the end of the row. A control in it can call `event.preventDefault()` on click to
   * close the menu without selecting the row.
   */
  rightSection?: ReactNode;
};

const DROPDOWN_MAX_HEIGHT = 240;
const SEARCH_MIN_OPTIONS = 6;

type Props = Pick<MenuProps, 'position' | 'width' | 'withinPortal'> & {
  target: ReactNode;
  options: SearchableMenuOption[];
  onSelectOption: (id: string) => void;
  optionNoun: string;
  footer?: ReactNode;
};

export function SearchableMenu({
  target,
  options,
  onSelectOption,
  optionNoun,
  footer,
  ...menuProps
}: Props) {
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();
  const filteredOptions = query
    ? options.filter(option => option.label.toLowerCase().includes(query))
    : options;
  const withSearch = options.length >= SEARCH_MIN_OPTIONS;

  return (
    <Menu
      {...menuProps}
      // Mantine autofocuses its own placeholder element first, which would leave the
      // search box needing a second click before you can type in it.
      withInitialFocusPlaceholder={!withSearch}
      // Resetting on `onClose` would flash the unfiltered list back in while the
      // dropdown is still fading out.
      onExitTransitionEnd={() => setSearch('')}
      styles={{ itemLabel: { minWidth: 0, overflowWrap: 'anywhere' } }}
    >
      <Menu.Target>{target}</Menu.Target>
      <Menu.Dropdown>
        {withSearch && (
          <TextInput
            data-autofocus
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder={`Search ${optionNoun}s`}
            aria-label={`Search ${optionNoun}s`}
            leftSection={<IconSearch size={14} />}
            size="xs"
            mb="xxs"
          />
        )}
        {options.length === 0 && <Menu.Item disabled>No options available</Menu.Item>}
        {options.length > 0 && filteredOptions.length === 0 && (
          <Menu.Item disabled>No matching {optionNoun}s</Menu.Item>
        )}
        <ScrollArea.Autosize mah={DROPDOWN_MAX_HEIGHT} type="auto">
          {filteredOptions.map(option =>
            option.rightSection ? (
              <OptionWithRightSection
                key={option.id}
                option={option}
                onSelect={() => onSelectOption(option.id)}
              />
            ) : (
              <Menu.Item key={option.id} onClick={() => onSelectOption(option.id)}>
                {option.label}
              </Menu.Item>
            ),
          )}
        </ScrollArea.Autosize>
        {footer && (
          <>
            <Menu.Divider />
            {footer}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

// A right section may hold its own buttons, and a button can't sit inside the row's button, so
// these rows render as a div and take over the Enter and Space selection a button gives for free.
function OptionWithRightSection({
  option,
  onSelect,
}: {
  option: SearchableMenuOption;
  onSelect: () => void;
}) {
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return;
    onSelect();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.currentTarget.click();
  };

  return (
    <Menu.Item
      component="div"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      rightSection={option.rightSection}
    >
      {option.label}
    </Menu.Item>
  );
}
